import { Field, GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { ComponentProps } from 'lib/component-props';
import { getItem } from 'lib/graphql/get-item';
import React from 'react';
import ParentItemWithChildrenQuery from './ParentChildSide.graphql';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { SvgIcon } from 'helpers/SvgIcon';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import { useRouter } from 'next/router';

type ParentChildSideNavItem = {
  hideFromParentChildNavigation: {
    value: boolean;
  };
  title: {
    jsonValue: Field<string>;
  };
  url: {
    url: string;
    path: string;
  };
};

type ParentChildSideNavProps = ComponentProps & {
  staticProps: {
    item: {
      parent: {
        children: {
          results: Array<ParentChildSideNavItem>;
        };
      } & ParentChildSideNavItem;
    };
  };
};

const ParentChildSideNav: React.FC<ParentChildSideNavProps> = (props) => {
  const items = props?.staticProps?.item?.parent?.children?.results;
  const router = useRouter();
  const { getDictionaryValue } = useDictionary();
  const [isOpen, setIsOpen] = React.useState(false);
  const { currentScreenWidth } = useCurrentScreenType();
  const triggerButtonRef = React.useRef<HTMLButtonElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Focus trap
  React.useEffect(() => {
    if (isOpen && currentScreenWidth < 768 && dialogRef.current) {
      const dialogNode = dialogRef.current;
      // Move focus to the dialog
      const focusableEls = dialogNode.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableEls.length) {
        focusableEls[1].focus();
      }
      // Trap focus
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleToggleButton(false);
        }
        if (e.key === 'Tab') {
          const firstEl = focusableEls[0];
          const lastEl = focusableEls[focusableEls.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
      };
      dialogNode.addEventListener('keydown', handleKeyDown);
      return () => {
        dialogNode.removeEventListener('keydown', handleKeyDown);
      };
    }
    // On close, return focus to trigger
    if (!isOpen && triggerButtonRef.current) {
      triggerButtonRef.current.focus();
    }
    return undefined;
  }, [isOpen, currentScreenWidth]);

  const activeItems = items?.filter((item) => {
    const currentPath = router.asPath?.split('/').reverse()[0]?.toLowerCase();
    const itemPath = item.url?.url.split('/').reverse()[0]?.toLowerCase();
    return currentPath === itemPath;
  });

  const handleToggleButton = (open: boolean) => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = open ? 'hidden' : 'auto';
    }
    setIsOpen(open);
  };

  // Accessibility: aria-label for nav
  return (
    <nav aria-label="In This Section" className="max-w-full theme-white">
      {currentScreenWidth < 768 && (
        <ButtonWrapper
          ctaVariant="secondary"
          onClick={() => handleToggleButton(true)}
          className="w-full block md:hidden min-h-[44px]"
          aria-expanded={isOpen}
          aria-controls="in-this-section-dialog"
          ref={triggerButtonRef}
        >
          <SvgIcon
            fill="none"
            icon="hamburger-menu"
            aria-label="hamburger-menu"
            viewBox="9 12 30 30"
            className="fill-gray-900 !w-[22px] !h-[22px] me-2"
          />
          <PlainTextWrapper field={{ value: getDictionaryValue('InThisSection') }} />
        </ButtonWrapper>
      )}

      <div
        ref={dialogRef}
        id="in-this-section-dialog"
        className={
          currentScreenWidth < 768
            ? isOpen
              ? 'fixed top-0 left-0 w-full h-full bg-white z-50 flex flex-col gap-0'
              : 'hidden'
            : 'block'
        }
        {...(currentScreenWidth < 768 && isOpen
          ? {
              role: 'dialog',
              'aria-modal': 'true',
              'aria-label': getDictionaryValue('InThisSection'),
            }
          : {})}
        tabIndex={-1}
      >
        {currentScreenWidth < 768 && (
          <div className="flex justify-between items-center border-b border-gray-100 p-10">
            <PlainTextWrapper
              field={{ value: getDictionaryValue('InThisSection') }}
              tag="h4"
              className="text-20 leading-[28px] font-bold font-centuryGothic text-gray-900"
              id="in-this-section-title"
            />
            <ButtonWrapper
              ctaVariant="link"
              onClick={() => handleToggleButton(false)}
              className="text-blue-base min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close dialog"
            >
              <SvgIcon
                fill="none"
                icon="close"
                aria-label="close"
                viewBox="0 0 24 24"
                className="fill-blue-base !w-[22px] !h-[22px] me-2"
              />
            </ButtonWrapper>
          </div>
        )}

        <ul className="py-16 px-10 md:p-0 h-full overflow-y-auto">
          <li className="w-full relative before:content-[''] before:absolute before:left-0 before:top-full before:w-full before:h-1 before:bg-gray-50 mb-1 p-1">
            <LinkWrapper
              field={{
                value: {
                  href: props?.staticProps?.item?.parent?.url?.url,
                },
              }}
              onClick={() => handleToggleButton(false)}
              className="block w-full text-20 py-11 leading-[28px] font-semibold font-centuryGothic text-gray-500 hover:underline hover:text-blue-base min-h-[44px]"
            >
              <PlainTextWrapper field={props?.staticProps?.item?.parent?.title?.jsonValue} />
            </LinkWrapper>
          </li>
          {items?.map((item, index) => {
            const isActive =
              activeItems?.[0]?.url?.url.split('/').reverse()[0].toLowerCase() ===
              item?.url?.url.split('/').reverse()[0].toLowerCase();
            if (item?.hideFromParentChildNavigation?.value) {
              return null;
            }
            return (
              <li
                key={`link-${index}`}
                className="w-full relative before:content-[''] before:absolute before:left-0 before:top-full before:w-full before:h-1 before:bg-gray-50 mb-1 p-1"
              >
                <LinkWrapper
                  field={{
                    value: {
                      href: item?.url?.url,
                      text: item?.title?.jsonValue?.value,
                      target: '_self',
                      title: item?.title?.jsonValue?.value,
                    },
                  }}
                  onClick={() => handleToggleButton(false)}
                  className={`relative block w-full text-14 px-8 py-11 leading-[20px] font-bold font-centuryGothic hover:underline hover:text-blue-base min-h-[44px] ${
                    isActive ? 'text-blue-base before:bg-blue-base' : 'text-gray-500'
                  } before:content-[''] before:absolute before:left-3 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-1/2 before:rounded-full`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <PlainTextWrapper field={item?.title?.jsonValue} />
                </LinkWrapper>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export const Default = withStandardComponentWrapper(ParentChildSideNav, false);

export const getStaticProps: GetStaticComponentProps = async (_, layoutData) => {
  const { itemId } = layoutData?.sitecore?.route || {};
  const { language } = layoutData?.sitecore?.context || {};

  if (!itemId) return { staticProps: null };
  const data = await getItem(ParentItemWithChildrenQuery, itemId, language);

  return {
    staticProps: data,
  };
};
